import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Moon, Sun } from 'lucide-react';
import { cn, Button } from '@tailoring-crm/ui';

type WorkspaceThemeContextValue = {
  dark: boolean;
  toggle: () => void;
};

const WorkspaceThemeContext = createContext<WorkspaceThemeContextValue | null>(null);
const WORKSPACE_THEME_STORAGE_KEY = 'tailoring-crm:leads-theme';

/**
 * The customer workspace (the customer book, the pipeline and the follow-ups) gets its own
 * presentation mode while the wider CRM keeps its current appearance. Persisting this small
 * preference means a rep does not have to reset it on every visit, without making it part of
 * application data or touching the server.
 *
 * The storage key keeps its original `leads-theme` name on purpose: renaming it would silently
 * reset everybody's saved choice.
 */
export function WorkspaceThemeSurface({
  children,
  className,
  fillsViewport = false,
}: {
  children: ReactNode;
  className?: string;
  /**
   * Ask the app shell for the window rather than the page — see the note beside the `:has()`
   * rules in `app-shell`. The customer book and the pipeline board ask; the follow-up list
   * deliberately does not, because it is read by scrolling.
   */
  fillsViewport?: boolean;
}): ReactElement {
  const [dark, setDark] = useState(() => {
    if (typeof window === 'undefined') return false;

    try {
      return window.localStorage.getItem(WORKSPACE_THEME_STORAGE_KEY) === 'dark';
    } catch {
      return false;
    }
  });

  const toggle = (): void => {
    setDark((current) => {
      const next = !current;

      try {
        window.localStorage.setItem(WORKSPACE_THEME_STORAGE_KEY, next ? 'dark' : 'light');
      } catch {
        // Private browsing can deny storage. The in-session toggle still works.
      }

      return next;
    });
  };

  // Dialogs render in a portal outside the surface. This scoped marker carries only the
  // workspace dialog palette across that boundary; it does not switch the rest of the CRM to dark
  // mode.
  useEffect(() => {
    document.body.dataset.workspaceTheme = dark ? 'dark' : 'light';

    return () => {
      delete document.body.dataset.workspaceTheme;
    };
  }, [dark]);

  return (
    <WorkspaceThemeContext.Provider value={{ dark, toggle }}>
      <div
        {...(fillsViewport ? { 'data-fills-viewport': '' } : {})}
        className={cn('atelier-workspace', dark && 'dark', className)}
      >
        {children}
      </div>
    </WorkspaceThemeContext.Provider>
  );
}

export function WorkspaceThemeToggle(): ReactElement {
  const theme = useContext(WorkspaceThemeContext);

  if (!theme) {
    throw new Error('WorkspaceThemeToggle must be used within WorkspaceThemeSurface.');
  }

  const label = theme.dark ? 'Use light appearance' : 'Use dark appearance';

  return (
    <Button
      variant="outline"
      size="icon"
      className="rounded-full"
      aria-label={label}
      aria-pressed={theme.dark}
      title={label}
      onClick={theme.toggle}
    >
      {theme.dark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
    </Button>
  );
}
